"use client";

import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { useListingFacetCounts } from "@/app/(saas)/(app)/businesses/_components/filters/use-listing-facet-counts";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Icon, type SupportedIcon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { api } from "@/lib/api-client";
import type { IndustryCategory } from "@/lib/types";
import { matchScore } from "@/lib/match-score";
import { cn } from "@/lib/utils";
import { useAtom } from "jotai";
import { ChevronDownIcon, ChevronRightIcon, LoaderCircleIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { Fragment, type PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";

type CheckboxTileProps = PropsWithChildren<{
  id?: string;
  icon?: SupportedIcon;
  isCategory?: boolean;
  count?: number;
  countsLoading?: boolean;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}>;

function CheckboxTile({
  id,
  icon,
  isCategory,
  count,
  countsLoading,
  checked,
  onCheckedChange,
  children,
}: CheckboxTileProps) {
  return (
    <Label
      className="min-h-11 px-4 py-2 flex items-center gap-3 cursor-pointer"
      htmlFor={id}
    >
      {icon && <Icon icon={icon} className="size-4 shrink-0" />}

      <div
        className={cn("flex-1 font-medium", {
          "text-sm": isCategory,
          "text-[13px]": !isCategory,
        })}
      >
        {children}
      </div>

      <div className="min-w-[2ch] text-right font-data text-xs tabular-nums text-muted-foreground">
        {countsLoading ? (
          <span className="inline-block w-6 h-3 bg-muted animate-pulse rounded" />
        ) : (
          count ?? 0
        )}
      </div>

      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="w-5 h-5 border-2 rounded-[6px]"
      />
    </Label>
  );
}

export function BusinessTypeFilter() {
  const t = useTranslations();

  const [filters, setFilters] = useAtom(businessFiltersAtom);
  const [searchValue, setSearchValue] = useState("");
  const [expandedEmptyCategories, setExpandedEmptyCategories] = useState<Set<number>>(new Set());

  const toggleEmptyCategory = useCallback((categoryId: number) => {
    setExpandedEmptyCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  }, []);

  const { data, isLoading } = api.listings.getIndustryCategories.useQuery(
    undefined,
    {
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const [filteredCategories, setFilteredCategories] = useState<IndustryCategory[]>([]);
  const facets = useListingFacetCounts();

  useEffect(() => {
    if (!data) {
      return;
    }

    const term = searchValue.toLowerCase().trim();

    if (!term) {
      setFilteredCategories(data.industryCategories);
      return;
    }

    const newFilteredCategories = data.industryCategories
      .map((category) => ({
        ...category,
        businessTypes: category.businessTypes.filter(
          (type) => matchScore(type.name, term, type.keywords) > 0,
        ),
      }))
      .filter((category) => category.businessTypes.length > 0);

    setFilteredCategories(newFilteredCategories);
  }, [data, searchValue]);

  function onIndustryCategoryCheckedChange(
    category: IndustryCategory,
    value: boolean,
  ) {
    const typeIds = category.businessTypes.map((type) => type.id);

    if (value) {
      setFilters((prev) => ({
        ...prev,
        businessTypeIds: Array.from(new Set([...prev.businessTypeIds, ...typeIds])),
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        businessTypeIds: prev.businessTypeIds.filter(
          (id) => !typeIds.includes(id),
        ),
      }));
    }
  }

  function onBusinessTypeCheckedChange(
    type: IndustryCategory["businessTypes"][number],
    value: boolean,
  ) {
    if (value) {
      setFilters((prev) => ({
        ...prev,
        businessTypeIds: Array.from(new Set([...prev.businessTypeIds, type.id])),
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        businessTypeIds: prev.businessTypeIds.filter((id) => id !== type.id),
      }));
    }
  }

  function onWantsToRemoveBusinessType(typeId: number) {
    setFilters((prev) => ({
      ...prev,
      businessTypeIds: prev.businessTypeIds.filter((id) => id !== typeId),
    }));
  }

  function resetBusinessTypes() {
    setFilters((prev) => ({
      ...prev,
      businessTypeIds: [],
    }));
    setSearchValue("");
  }

  const flatTypes = useMemo(
    () =>
      (data?.industryCategories ?? []).flatMap((category) =>
        category.businessTypes.map((type) => ({
          ...type,
          categoryName: category.name,
        })),
      ),
    [data],
  );

  const businessTypeCounts = facets.data?.businessTypeCounts ?? {};

  const searchedTypes = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (term.length < 2) return [];
    return flatTypes
      .map((type) => ({ ...type, _score: matchScore(type.name, term, type.keywords) }))
      .filter((type) => type._score > 0)
      .sort((a, b) => {
        const hasA = Number(businessTypeCounts[String(a.id)] ?? 0) > 0 ? 1 : 0;
        const hasB = Number(businessTypeCounts[String(b.id)] ?? 0) > 0 ? 1 : 0;
        if (hasA !== hasB) return hasB - hasA;
        return b._score - a._score;
      })
      .slice(0, 25);
  }, [flatTypes, searchValue, businessTypeCounts]);

  const selectedBusinessTypes = useMemo(
    () => flatTypes.filter((type) => filters.businessTypeIds.includes(type.id)),
    [flatTypes, filters.businessTypeIds],
  );

  const categoryCounts = useMemo(() => {
    const categories = data?.industryCategories ?? [];
    const counts = new Map<number, number>();

    for (const category of categories) {
      let total = 0;
      for (const type of category.businessTypes) {
        total += Number(businessTypeCounts[String(type.id)] ?? 0);
      }
      counts.set(category.id, total);
    }

    return counts;
  }, [data?.industryCategories, businessTypeCounts]);

  function getBusinessTypeCount(typeId: number) {
    return Number(businessTypeCounts[String(typeId)] ?? 0);
  }

  const sortedFilteredCategories = useMemo(() => {
    return [...filteredCategories]
      .map((category) => ({
        ...category,
        businessTypes: [...category.businessTypes].sort((a, b) => {
          const countA = Number(businessTypeCounts[String(a.id)] ?? 0);
          const countB = Number(businessTypeCounts[String(b.id)] ?? 0);
          if (countA > 0 && countB === 0) return -1;
          if (countA === 0 && countB > 0) return 1;
          return countB - countA;
        }),
      }))
      .sort((a, b) => {
        const countA = categoryCounts.get(a.id) ?? 0;
        const countB = categoryCounts.get(b.id) ?? 0;
        if (countA > 0 && countB === 0) return -1;
        if (countA === 0 && countB > 0) return 1;
        return countB - countA;
      });
  }, [filteredCategories, businessTypeCounts, categoryCounts]);

  return (
    <>
      <div className="sticky bg-background">
        <div className="p-2 flex flex-col gap-2">
          <Input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={t(
              "saas.search.filters.fields.businessTypes.search.placeholder",
            )}
            className="h-10"
          />

          {selectedBusinessTypes.length > 0 && (
            <div className="rounded-md border bg-muted/30 p-2">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  {selectedBusinessTypes.length} selected
                </span>
                <button
                  type="button"
                  onClick={resetBusinessTypes}
                  className="text-xs text-muted-foreground transition-colors hover:text-foreground hover:underline"
                >
                  Clear all
                </button>
              </div>

              <div className="max-h-20 overflow-y-auto pr-1">
                <div className="flex flex-wrap gap-2">
                  {selectedBusinessTypes.map((type) => (
                    <Button
                      key={type.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onWantsToRemoveBusinessType(type.id)}
                      className="h-8 max-w-full gap-1.5"
                    >
                      <span className="max-w-[220px] truncate">{type.name}</span>
                      <Icon icon="XClose" className="size-4 shrink-0" />
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-2 pb-2">
          <span className="text-xs text-muted-foreground">
            {searchValue.trim().length >= 2
              ? `${searchedTypes.length} business types`
              : `${flatTypes.length} business types`}
          </span>
          <span className="text-xs text-muted-foreground">
            {facets.isLoading ? (
              <span className="inline-block w-8 h-3 bg-muted animate-pulse rounded" />
            ) : (
              <span className="font-data tabular-nums text-foreground">
                {facets.data?.total ?? 0}
              </span>
            )}{" "}
            match
          </span>
        </div>
        <Separator />
      </div>

      <div className="max-h-full md:max-h-[600px] overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <LoaderCircleIcon className="w-10 h-10 animate-spin" />
          </div>
        )}

        {searchValue.trim().length >= 2 ? (
          <div className="flex flex-col">
            {searchedTypes.map((type, idx) => (
              <Fragment key={type.id}>
                <SearchTypeRow
                  id={type.id}
                  iconName={type.iconName ?? null}
                  name={type.name}
                  categoryName={type.categoryName ?? "Other"}
                  count={getBusinessTypeCount(type.id)}
                  isLoading={facets.isLoading}
                  checked={filters.businessTypeIds.includes(type.id)}
                  onCheckedChange={(value) => onBusinessTypeCheckedChange(type, value)}
                />
                {idx < searchedTypes.length - 1 && <Separator />}
              </Fragment>
            ))}
          </div>
        ) : (
          <div className="flex flex-col">
            {sortedFilteredCategories.map((category, idx) => {
              const facetsLoaded = !!facets.data;
              const withResults = facetsLoaded
                ? category.businessTypes.filter((t) => getBusinessTypeCount(t.id) > 0)
                : category.businessTypes;
              const withoutResults = facetsLoaded
                ? category.businessTypes.filter((t) => getBusinessTypeCount(t.id) === 0)
                : [];
              const isExpanded = expandedEmptyCategories.has(category.id);
              const selectedEmptyCount = withoutResults.filter((t) =>
                filters.businessTypeIds.includes(t.id),
              ).length;

              return (
                <Fragment key={category.id}>
                  {idx > 0 && <Separator />}

                  <CheckboxTile
                    id={`category-${category.id}`}
                    isCategory
                    count={categoryCounts.get(category.id) ?? 0}
                    countsLoading={facets.isLoading}
                    checked={category.businessTypes.every((type) =>
                      filters.businessTypeIds.includes(type.id),
                    )}
                    onCheckedChange={(value) =>
                      onIndustryCategoryCheckedChange(category, value)
                    }
                  >
                    {t("saas.search.filters.fields.businessTypes.all", {
                      category: category.name,
                    })}
                  </CheckboxTile>

                  <Separator />

                  {withResults.map((type, typeIdx) => (
                    <Fragment key={type.id}>
                      <CheckboxTile
                        id={`type-${type.id}`}
                        icon={(type.iconName ? `BusinessType.${type.iconName}` : "Building2Solid") as never}
                        count={getBusinessTypeCount(type.id)}
                        countsLoading={facets.isLoading}
                        checked={filters.businessTypeIds.includes(type.id)}
                        onCheckedChange={(value) =>
                          onBusinessTypeCheckedChange(type, value)
                        }
                      >
                        {type.name}
                      </CheckboxTile>
                      {typeIdx < withResults.length - 1 && <Separator />}
                    </Fragment>
                  ))}

                  {withoutResults.length > 0 && (
                    <>
                      {withResults.length > 0 && <Separator />}
                      <button
                        type="button"
                        onClick={() => toggleEmptyCategory(category.id)}
                        className="flex w-full items-center gap-2 px-4 py-2 text-xs text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
                      >
                        {isExpanded ? (
                          <ChevronDownIcon className="size-3.5" />
                        ) : (
                          <ChevronRightIcon className="size-3.5" />
                        )}
                        <span>
                          {withoutResults.length} more with 0 listings
                          {selectedEmptyCount > 0 && (
                            <span className="ml-1 text-foreground font-medium">
                              ({selectedEmptyCount} selected)
                            </span>
                          )}
                        </span>
                      </button>

                      {isExpanded && (
                        <>
                          <Separator />
                          {withoutResults.map((type, typeIdx) => (
                            <Fragment key={type.id}>
                              <CheckboxTile
                                id={`type-${type.id}`}
                                icon={(type.iconName ? `BusinessType.${type.iconName}` : "Building2Solid") as never}
                                count={0}
                                countsLoading={facets.isLoading}
                                checked={filters.businessTypeIds.includes(type.id)}
                                onCheckedChange={(value) =>
                                  onBusinessTypeCheckedChange(type, value)
                                }
                              >
                                {type.name}
                              </CheckboxTile>
                              {typeIdx < withoutResults.length - 1 && <Separator />}
                            </Fragment>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </Fragment>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

BusinessTypeFilter.displayName = "BusinessTypeFilter";

function SearchTypeRow({
  id,
  iconName,
  name,
  categoryName,
  count,
  isLoading,
  checked,
  onCheckedChange,
}: {
  id: number;
  iconName: string | null;
  name: string;
  categoryName: string;
  count: number;
  isLoading: boolean;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
}) {
  return (
    <Label
      htmlFor={`type-search-${id}`}
      className={cn(
        "flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors hover:bg-muted/40",
        checked && "bg-muted/30",
      )}
    >
      <Icon
        icon={(iconName ? `BusinessType.${iconName}` : "Building2Solid") as never}
        className="size-4 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{name}</div>
        <div className="text-xs text-muted-foreground truncate">{categoryName}</div>
      </div>
      <div className="min-w-[2ch] text-right font-data text-xs tabular-nums text-muted-foreground">
        {isLoading ? (
          <span className="inline-block w-6 h-3 bg-muted animate-pulse rounded" />
        ) : (
          count
        )}
      </div>
      <Checkbox
        id={`type-search-${id}`}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="w-5 h-5 border-2 rounded-[6px]"
      />
    </Label>
  );
}
